import { left, right } from 'fp-ts/Either';
import { httpClientFunction } from 'domain/types';
import { WidgetType } from 'domain/entity/widget';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { ChartMapper, JsonChartData } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import {
    asImageWidget,
    asTextWidget,
    JsonWidgetData,
    JsonWidgetDataError,
    JsonWidgetImageData,
    JsonWidgetTextData,
} from 'adapter/out/client/widgets/data/mappers/common';
import { asGaugeWidget, JsonWidgetGaugeData } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';
import { asHeatmapData, JsonHeatmapData } from 'adapter/out/client/widgets/data/mappers/HeatmapMapper';
import { asPieData, JsonPieData } from 'adapter/out/client/widgets/data/mappers/PieMapper';
import { JsonSingleKPIData, singleKPIMapper } from 'adapter/out/client/widgets/data/mappers/singleKPIMapper';
import { asTableWidget, JsonTable } from 'adapter/out/client/widgets/data/mappers/TableMapper';
import ReadWidgetDataClient from 'use-case/widgets/data/read/readWidgetDataClient';
import ReadWidgetDataCriteria from 'use-case/widgets/data/read/readWidgetDataCriteria';

const readWidgetDataApiClient = (
    httpClient: httpClientFunction,
    baseUrl: string,
    buildUrl: (criteria: ReadWidgetDataCriteria) => string = () => '/dashboards'
): ReadWidgetDataClient => (criteria: ReadWidgetDataCriteria) => {
    const isProxyBaseUrl = baseUrl.includes('/proxy/api-v3');
    let url = `${baseUrl}${buildUrl(criteria)}/${criteria.destination}/${criteria.widgetId}/data`;

    if (criteria.reportInstanceId) {
        url = isProxyBaseUrl
            ? `${baseUrl}/dashboards/${criteria.destination}/${criteria.widgetId}/data`
            : `${baseUrl}${buildUrl(criteria)}/reports/${criteria.reportInstanceId}/proxy/api-v3/dashboards/${criteria.destination}/${criteria.widgetId}/data`;
    }

    return httpClient(url, {})
        .then(async (response) =>
            response.ok
                ? response
                      .json()
                      .then(asWidgetData)
                      .then(right)
                      .catch((error: Error) => asWidgetDataError(error, 500))
                : asWidgetDataError(await response.json(), response.status)
        )
        .catch((error: Error) => asWidgetDataError(error, 500));
};

export const asWidgetDataError = (json: JsonWidgetDataError, status: number) => left({ error: new Error(json.message), status });

export const asWidgetData = (json: JsonWidgetData): WidgetDataTypes => {
    switch (json.type) {
        case WidgetType.SINGLE_KPI:
            return singleKPIMapper(json as JsonSingleKPIData);
        case WidgetType.LINE:
        case WidgetType.BAR:
        case WidgetType.STACKED_BAR:
            return ChartMapper(json as JsonChartData);
        case WidgetType.PIE:
            return asPieData(json as JsonPieData);
        case WidgetType.HEATMAP:
        case WidgetType.MULTI_DEVICE_HEATMAP:
            return asHeatmapData(json as JsonHeatmapData);
        case WidgetType.TEXT:
            return asTextWidget((json as unknown) as JsonWidgetTextData);
        case WidgetType.IMAGE:
            return asImageWidget((json as unknown) as JsonWidgetImageData);
        case WidgetType.GAUGE:
            return asGaugeWidget(json as JsonWidgetGaugeData);
        case WidgetType.TABLE:
            return asTableWidget(json as JsonTable);
        default:
            throw new Error(`Missing AsWidgetData WidgetType Mapper implementation for ${json.type}`);
    }
};

export default readWidgetDataApiClient;
